import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertTriangle,
  ShieldAlert,
  Clock,
  UserX,
  Settings,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  Flag,
  UserMinus,
} from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import FindingCrossLinks from './FindingCrossLinks';

// Layout for each bucket — order is intentional (worst first)
const BUCKETS = [
  {
    key: 'admin_critical',
    label: 'Admin · Critical',
    description: 'Admins inactive ≥ threshold',
    icon: ShieldAlert,
    tone: 'critical',
  },
  {
    key: 'stale_threshold',
    label: '≥ threshold days',
    description: 'Non-admins inactive ≥ threshold',
    icon: AlertTriangle,
    tone: 'high',
  },
  {
    key: 'stale_60_89',
    label: '60–89 days',
    description: 'Approaching threshold',
    icon: Clock,
    tone: 'medium',
  },
  {
    key: 'stale_30_59',
    label: '30–59 days',
    description: 'Watchlist',
    icon: Clock,
    tone: 'low',
  },
  {
    key: 'never_logged_in',
    label: 'Never logged in',
    description: 'Past onboarding grace',
    icon: UserX,
    tone: 'medium',
  },
];

const TONE_STYLES = {
  critical: 'bg-red-50 border-red-200 text-red-900',
  high: 'bg-amber-50 border-amber-200 text-amber-900',
  medium: 'bg-orange-50 border-orange-200 text-orange-900',
  low: 'bg-slate-50 border-slate-200 text-slate-700',
};

const STATUS_BADGE = {
  passed: { className: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Passed', icon: CheckCircle },
  insufficient_evidence: { className: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Needs review', icon: AlertTriangle },
  failed: { className: 'bg-red-100 text-red-800 border-red-200', label: 'Failed', icon: AlertTriangle },
};

const InactiveAccountsWidget = ({ workspaceId, gwConnected, userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // bucket key currently open
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [thresholdInput, setThresholdInput] = useState('90');
  const [graceInput, setGraceInput] = useState('14');
  const [savingSettings, setSavingSettings] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const resp = await axios.get(`/api/employees/inactive-accounts`, {
        params: { workspace_id: workspaceId },
      });
      setData(resp.data);
      setThresholdInput(String(resp.data.threshold_days));
      setGraceInput(String(resp.data.grace_days));
    } catch (err) {
      console.error('inactive-accounts load failed', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      const payload = {};
      const t = parseInt(thresholdInput, 10);
      const g = parseInt(graceInput, 10);
      if (Number.isFinite(t)) payload.inactive_account_threshold_days = t;
      if (Number.isFinite(g)) payload.never_logged_in_grace_days = g;
      await axios.put(`/api/workspace-settings/inactive-accounts`, payload, {
        params: { workspace_id: workspaceId },
      });
      toast.success('Threshold updated');
      setSettingsOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update threshold');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleMarkNonEmployee = async (sourceUserId, primaryEmail) => {
    try {
      setMarkingId(sourceUserId);
      await axios.patch(`/api/employees/${encodeURIComponent(primaryEmail)}/identity`, {
        identity_type: 'non_employee',
        reason: 'Reclassified as non-employee from Inactive Accounts Review',
      });
      toast.success('Marked as non-employee');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update classification');
    } finally {
      setMarkingId(null);
    }
  };

  // Don't render the widget at all if GW isn't connected — there's no
  // directory data to evaluate. The Employees tab still has the
  // existing stats cards for manual accounts.
  if (!gwConnected) return null;

  const statusKey = data?.status || 'passed';
  const statusMeta = STATUS_BADGE[statusKey] || STATUS_BADGE.passed;
  const StatusIcon = statusMeta.icon;

  return (
    <Card className="border-slate-200 mb-6" data-testid="inactive-accounts-widget">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Inactive Accounts Review
              {data && (
                <Badge className={`${statusMeta.className} inline-flex items-center gap-1`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusMeta.label}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              SOC 2 CC6.2 · ISO 27001 A.5.18
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={load}
              variant="ghost"
              size="sm"
              disabled={loading}
              data-testid="inactive-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={() => setSettingsOpen(true)}
              variant="outline"
              size="sm"
              data-testid="inactive-settings-btn"
            >
              <Settings className="w-4 h-4 mr-1" />
              {data ? `${data.threshold_days}d threshold` : 'Settings'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !data && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Evaluating directory…
          </div>
        )}

        {data && (
          <>
            {(() => {
              const b = data.buckets || {};
              const flagged = (b.admin_critical?.count || 0) + (b.stale_threshold?.count || 0);
              const neverIn = b.never_logged_in?.count || 0;
              const passed = statusKey === 'passed';
              const strip = passed
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : statusKey === 'failed'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-amber-50 border-amber-200 text-amber-800';
              return (
                <div className={`flex items-start gap-2 border rounded-md px-3 py-2 mb-4 ${strip}`} data-testid="inactive-result">
                  {passed
                    ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                  <p className="text-sm font-medium">
                    {passed
                      ? `All active accounts have signed in within ${data.threshold_days} days. No action required.`
                      : `${flagged} account${flagged === 1 ? '' : 's'} inactive ≥ ${data.threshold_days} days${neverIn ? ` · ${neverIn} never signed in` : ''}.`}
                  </p>
                </div>
              );
            })()}

            {/* Bucket grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              {BUCKETS.map(({ key, label, icon: Icon, tone }) => {
                const bucket = data.buckets?.[key] || { count: 0, users: [] };
                const tile = TONE_STYLES[tone] || TONE_STYLES.low;
                const isOpen = expanded === key;
                const hasItems = bucket.count > 0;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => hasItems && setExpanded(isOpen ? null : key)}
                    disabled={!hasItems}
                    data-testid={`inactive-bucket-${key}`}
                    className={`border rounded-lg p-3 text-left transition-all ${tile} ${
                      hasItems ? 'hover:shadow-sm cursor-pointer' : 'opacity-60 cursor-default'
                    } ${isOpen ? 'ring-2 ring-offset-1 ring-slate-300' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Icon className="w-4 h-4" strokeWidth={1.7} />
                      <span className="text-2xl font-semibold leading-none">{bucket.count}</span>
                    </div>
                    <div className="text-xs font-medium">{label}</div>
                  </button>
                );
              })}
            </div>

            {/* Drill-down for expanded bucket */}
            {expanded && (
              <div className="border rounded-lg border-slate-200 bg-white" data-testid="inactive-drilldown">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                  <div className="text-xs font-medium text-slate-700">
                    {(BUCKETS.find(b => b.key === expanded) || {}).label} ·{' '}
                    {data.buckets?.[expanded]?.count} users
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpanded(null)}
                    className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                  >
                    Close <ChevronUp className="w-3 h-3" />
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {(data.buckets[expanded]?.users || []).map((u) => (
                    <div
                      key={u.source_user_id}
                      className="px-3 py-2 flex items-center justify-between gap-3 hover:bg-slate-50"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                          {u.full_name || u.primary_email}
                          {(u.is_admin || u.is_delegated_admin) && (
                            <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px] py-0">
                              Admin
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {u.primary_email} ·{' '}
                          {u.bucket === 'never_logged_in'
                            ? `Never logged in (${u.days_since}d since creation)`
                            : `${u.days_since} days since last login`}
                          {u.department ? ` · ${u.department}` : ''}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={markingId === u.source_user_id}
                        onClick={() => handleMarkNonEmployee(u.source_user_id, u.primary_email)}
                        data-testid={`mark-non-employee-${u.source_user_id}`}
                      >
                        {markingId === u.source_user_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <UserMinus className="w-3 h-3 mr-1" />
                            Mark as non-employee
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {statusKey !== 'passed' && data.what_would_fix && (
              <div className="mt-4 flex items-start gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="inactive-action">
                <Flag className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
                <span><span className="font-semibold text-slate-800">Action:</span> {data.what_would_fix}</span>
              </div>
            )}

            <FindingCrossLinks
              ruleKey="directory.access.inactive_account_review"
              matches={(data.flagged_users || []).map(u => ({ email: u.primary_email }))}
              workspaceId={workspaceId}
            />

            <div className="mt-3 text-[11px] text-slate-400">
              Evaluated {formatDate(data.evaluated_at)} ·{' '}
              {data.totals.active_users} active users · {data.totals.admin_count} admins
            </div>
          </>
        )}
      </CardContent>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inactive Account Thresholds</DialogTitle>
            <DialogDescription>
              Workspace-wide settings for the access-review widget and the
              audit auto-evidence rule. SOC 2 industry default: 90 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="threshold-input" className="text-xs">Inactive after (days)</Label>
              <Input
                id="threshold-input"
                type="number"
                min="7"
                max="365"
                value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
                className="mt-1"
                data-testid="threshold-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">7 – 365 days.</p>
            </div>
            <div>
              <Label htmlFor="grace-input" className="text-xs">Onboarding grace for never-logged-in (days)</Label>
              <Input
                id="grace-input"
                type="number"
                min="0"
                max="90"
                value={graceInput}
                onChange={(e) => setGraceInput(e.target.value)}
                className="mt-1"
                data-testid="grace-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                New accounts under this age won't be flagged as "never logged in".
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="bg-navy-600 hover:bg-navy-700"
              data-testid="threshold-save-btn"
            >
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InactiveAccountsWidget;
